import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useState,
} from 'react';
import { Dayjs } from 'dayjs';
import {
  FixedStrikeConfig,
  MatrixColumnData,
  MatrixData,
  RawGreeksDataMap,
} from 'types';
import {
  getQueryDate,
  getCurrentDate,
  isMarketOpenOnDate,
  prevBusinessDayOpenMarket,
} from 'util/shared';

interface FixedStrikeContextValue {
  config: FixedStrikeConfig;
  updateConfig: (patch: Partial<FixedStrikeConfig>) => void;

  currentData: MatrixData[] | undefined;
  setCurrentData: Dispatch<SetStateAction<MatrixData[] | undefined>>;
  columnData: MatrixColumnData[];
  setColumnData: Dispatch<SetStateAction<MatrixColumnData[]>>;
  currentGreeks: RawGreeksDataMap | null;
  setCurrentGreeks: Dispatch<SetStateAction<RawGreeksDataMap | null>>;
  prevExpiryMap: RawGreeksDataMap | null;
  setPrevExpiryMap: Dispatch<SetStateAction<RawGreeksDataMap | null>>;
  currentDate: Dayjs;
  setCurrentDate: Dispatch<SetStateAction<Dayjs>>;
  compareDate: Dayjs;
  setCompareDate: Dispatch<SetStateAction<Dayjs>>;
  matrixExpirationRangeOpen: boolean;
  setMatrixExpirationRangeOpen: Dispatch<SetStateAction<boolean>>;
  matrixPercentOTMRangeOpen: boolean;
  setMatrixPercentOTMRangeOpen: Dispatch<SetStateAction<boolean>>;
  pruneRowsPercentSelectorOpen: boolean;
  setPruneRowsPercentSelectorOpen: Dispatch<SetStateAction<boolean>>;
  pruneColumnsPercentSelectorOpen: boolean;
  setPruneColumnsPercentSelectorOpen: Dispatch<SetStateAction<boolean>>;

  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  error: string | null;
  setError: Dispatch<SetStateAction<string | null>>;
}

const FixedStrikeContext = createContext<FixedStrikeContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export const useFixedStrikeCtx = (): FixedStrikeContextValue => {
  const ctx = useContext(FixedStrikeContext);
  if (ctx == null) {
    throw new Error(
      'useFixedStrikeCtx must be used within a FixedStrikeProvider',
    );
  }
  return ctx;
};

interface FixedStrikeProviderProps {
  config: FixedStrikeConfig;
  onConfigChange: (patch: Partial<FixedStrikeConfig>) => void;
  urlKeyPrefix?: string;
  children: ReactNode;
}

export const FixedStrikeProvider = ({
  config,
  onConfigChange,
  children,
}: FixedStrikeProviderProps) => {
  const [currentData, setCurrentData] = useState<MatrixData[] | undefined>(
    undefined,
  );
  const [columnData, setColumnData] = useState<MatrixColumnData[]>([]);
  const [currentGreeks, setCurrentGreeks] = useState<RawGreeksDataMap | null>(
    null,
  );
  const [prevExpiryMap, setPrevExpiryMap] = useState<RawGreeksDataMap | null>(
    null,
  );
  const [currentDate, setCurrentDate] = useState<Dayjs>(() =>
    getQueryDate(true),
  );
  const [compareDate, setCompareDate] = useState<Dayjs>(() =>
    isMarketOpenOnDate(getCurrentDate())
      ? prevBusinessDayOpenMarket()
      : prevBusinessDayOpenMarket(prevBusinessDayOpenMarket()),
  );

  // Selector open/close — local UI state
  const [matrixExpirationRangeOpen, setMatrixExpirationRangeOpen] =
    useState(false);
  const [matrixPercentOTMRangeOpen, setMatrixPercentOTMRangeOpen] =
    useState(false);
  const [pruneRowsPercentSelectorOpen, setPruneRowsPercentSelectorOpen] =
    useState(false);
  const [pruneColumnsPercentSelectorOpen, setPruneColumnsPercentSelectorOpen] =
    useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <FixedStrikeContext.Provider
      value={{
        config,
        updateConfig: onConfigChange,
        currentData,
        setCurrentData,
        columnData,
        setColumnData,
        currentGreeks,
        setCurrentGreeks,
        prevExpiryMap,
        setPrevExpiryMap,
        currentDate,
        setCurrentDate,
        compareDate,
        setCompareDate,
        matrixExpirationRangeOpen,
        setMatrixExpirationRangeOpen,
        matrixPercentOTMRangeOpen,
        setMatrixPercentOTMRangeOpen,
        pruneRowsPercentSelectorOpen,
        setPruneRowsPercentSelectorOpen,
        pruneColumnsPercentSelectorOpen,
        setPruneColumnsPercentSelectorOpen,
        loading,
        setLoading,
        error,
        setError,
      }}
    >
      {children}
    </FixedStrikeContext.Provider>
  );
};
